import { type PenaltyKick, type ProjectState, penaltyShootoutSchema } from "@livcg/schema";
import { Plus, RotateCcw } from "lucide-react";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, cn, focusRing } from "../../ui/index.js";
import { Panel } from "./Panel.js";

/* Direct choice for every kick (like the SKORE g/d/0 radios):
   G or X is set in one click, clicking the active one again returns it to pending.
   NOT a cycle — while the panel is on air a wrong intermediate state (e.g. G
   on the way to setting X) must never go on air. */

function fivePending(): PenaltyKick[] {
  return ["pending", "pending", "pending", "pending", "pending"];
}

type Props = { slug: string; state: ProjectState };

export function PenaltyPanel({ slug, state }: Props) {
  // Old projects may lack `penalties` — defaults via the schema.
  const pen = penaltyShootoutSchema.parse(state.penalties ?? {});
  const onAir = state.overlay.mode === "little_pen";

  const send = (home: PenaltyKick[], away: PenaltyKick[]): void =>
    void sendRaw("penalties.set", { slug, home, away });

  /** Sets an explicit state on a kick; if the active state is clicked again — pending. */
  const setKick = (side: "home" | "away", i: number, value: PenaltyKick): void => {
    const home = [...pen.home];
    const away = [...pen.away];
    const arr = side === "home" ? home : away;
    arr[i] = arr[i] === value ? "pending" : value;
    send(home, away);
  };

  const add = (): void => send([...pen.home, "pending"], [...pen.away, "pending"]);
  const reset = (): void => send(fivePending(), fivePending());
  const toggleAir = (): void => {
    // little_pen runs ON TOP of the scoreboard (the graphic forces it up) —
    // taking the panel off leaves the bare scoreboard (SKORE little semantics).
    if (onAir) void sendRaw("overlay.show", { slug, mode: "scoreboard", data: {} });
    else void sendRaw("overlay.show", { slug, mode: "little_pen", data: {} });
  };

  const homeGoals = pen.home.filter((k) => k === "goal").length;
  const awayGoals = pen.away.filter((k) => k === "goal").length;

  const row = (side: "home" | "away", kicks: PenaltyKick[], abbr: string, goals: number) => (
    <div className="flex items-center gap-2">
      <span className="w-10 shrink-0 truncate text-[11px] font-bold uppercase tracking-wider text-ink-muted">
        {abbr}
      </span>
      <span className="w-5 shrink-0 text-center text-[13px] font-bold tabular-nums text-ink">
        {goals}
      </span>
      <div className="flex flex-1 flex-wrap gap-1">
        {kicks.map((k, i) => (
          <div
            key={i}
            className="inline-flex h-7 overflow-hidden rounded-md border border-border"
            title={`${side} urish ${i + 1}`}
          >
            <button
              type="button"
              onClick={() => setKick(side, i, "goal")}
              className={cn(
                focusRing,
                "flex w-6 items-center justify-center text-[12px] font-bold transition-colors",
                k === "goal"
                  ? "bg-ready-strong text-white"
                  : "bg-surface text-ink-faint hover:text-success",
              )}
              aria-pressed={k === "goal"}
            >
              G
            </button>
            <button
              type="button"
              onClick={() => setKick(side, i, "miss")}
              className={cn(
                focusRing,
                "flex w-6 items-center justify-center border-l border-border text-[12px] font-bold transition-colors",
                k === "miss"
                  ? "bg-danger text-white"
                  : "bg-surface text-ink-faint hover:text-danger",
              )}
              aria-pressed={k === "miss"}
            >
              X
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const homeAbbr = state.teams.home?.code || state.teams.home?.shortName || "HOME";
  const awayAbbr = state.teams.away?.code || state.teams.away?.shortName || "AWAY";

  return (
    <Panel
      label="Penaltilar"
      action={
        <button
          type="button"
          onClick={toggleAir}
          className={cn(
            focusRing,
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors",
            onAir
              ? "border-live bg-live-soft text-live"
              : "border-border bg-surface-elevated text-ink-soft hover:text-ink",
          )}
        >
          <span className="relative flex h-1.5 w-1.5">
            {onAir && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-live/70 motion-reduce:hidden" />
            )}
            <span
              className={cn(
                "relative inline-flex h-1.5 w-1.5 rounded-full",
                onAir ? "bg-live" : "bg-ink-faint",
              )}
            />
          </span>
          {onAir ? "Efirda" : "Efirga"}
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        {row("home", pen.home, homeAbbr, homeGoals)}
        {row("away", pen.away, awayAbbr, awayGoals)}
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={add}
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            disabled={pen.home.length >= 15}
          >
            Urish qo'shish
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={reset}
            leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
          >
            Reset
          </Button>
        </div>
      </div>
    </Panel>
  );
}
